import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../Dashboard/Dashboard.css';
import './MeetingRecordDetail.css';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import { useAuth } from '../../contexts/AuthContext';
import { meetingRecordsAPI } from '../../services/meetingRecords';
import { useRecruiterBooth } from '../../hooks/useRecruiterBooth';
import { openResumeInNewTab } from '../../utils/resumeViewer';

export default function MeetingRecordDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    const { booth, event } = useRecruiterBooth();

    const [meetingRecord, setMeetingRecord] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState(null);
    const isRecruiter = user?.role === 'Recruiter';

    useEffect(() => {
        if (!loading) {
            if (!user) {
                navigate('/login', { replace: true });
            } else if (!['Admin', 'GlobalSupport', 'Recruiter'].includes(user.role)) {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [user, loading, navigate]);

    const loadMeetingRecord = useCallback(async () => {
        try {
            setLoadingData(true);
            const record = await meetingRecordsAPI.getMeetingRecord(id);
            setMeetingRecord(record);
        } catch (error) {
            console.error('Error loading meeting record:', error);
            setError('Failed to load meeting record');
        } finally {
            setLoadingData(false);
        }
    }, [id]);

    useEffect(() => {
        if (user && id) {
            loadMeetingRecord();
        }
    }, [user, id, loadMeetingRecord]);

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatDuration = (minutes) => {
        if (!minutes) return 'N/A';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins}m`;
        }
        return `${mins}m`;
    };

    const renderStars = (rating) => {
        if (!rating) return 'No rating';
        return '★'.repeat(rating) + '☆'.repeat(5 - rating);
    };

    const renderMessageType = (type) => {
        const typeMap = {
            text: 'Text Message',
            audio: 'Audio Message',
            video: 'Video Message'
        };
        return typeMap[type] || type;
    };

    const formatStatus = (status) => {
        const statusLabels = {
            'scheduled': 'Scheduled',
            'active': 'Active',
            'completed': 'Completed',
            'cancelled': 'Cancelled',
            'failed': 'Failed',
            'left_with_message': 'Left Message'
        };
        return statusLabels[status] || status;
    };

    if (loading || !user) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (loadingData) {
        return (
            <div className="dashboard">
                <a href="#main-content" className="skip-link">Skip to main content</a>
                <AdminHeader 
                    brandingLogo={event?.logoUrl || event?.logo || ''}
                    brandingLogoAlt={event?.logoAltText || ''}
                    secondaryLogo={booth?.logoUrl || booth?.companyLogo || ''}
                    secondaryLogoAlt={booth?.logoAltText || ''}
                />
                <div className="dashboard-layout">
                    <AdminSidebar active="meeting-records" />
                    <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
                        <div className="dashboard-content">
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Loading meeting record...</p>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    if (error || !meetingRecord) {
        return (
            <div className="dashboard">
                <a href="#main-content" className="skip-link">Skip to main content</a>
                <AdminHeader 
                    brandingLogo={event?.logoUrl || event?.logo || ''}
                    brandingLogoAlt={event?.logoAltText || ''}
                    secondaryLogo={booth?.logoUrl || booth?.companyLogo || ''}
                    secondaryLogoAlt={booth?.logoAltText || ''}
                />
                <div className="dashboard-layout">
                    <AdminSidebar active="meeting-records" />
                    <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
                        <div className="dashboard-content">
                            <div className="error-container">
                                <h2>Error</h2>
                                <p>{error || 'Meeting record not found'}</p>
                                <button
                                    className="btn-back"
                                    onClick={() => navigate('/meeting-records')}
                                    type="button"
                                >
                                    ← Back to Meeting Records
                                </button>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <a href="#main-content" className="skip-link">Skip to main content</a>
            <AdminHeader 
                brandingLogo={event?.logoUrl || event?.logo || ''}
                brandingLogoAlt={event?.logoAltText || ''}
                secondaryLogo={booth?.logoUrl || booth?.companyLogo || ''}
                secondaryLogoAlt={booth?.logoAltText || ''}
            />
            <div className="dashboard-layout">
                <AdminSidebar active="meeting-records" />
                <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
                    <div className="dashboard-content">
                        <div className="meeting-detail-container">
                            <div className="meeting-detail-header">
                                <div className="meeting-detail-header-left">
                                    <button
                                        className="btn-back"
                                        onClick={() => navigate('/meeting-records')}
                                        type="button"
                                    >
                                        ← Back to Meeting Records
                                    </button>
                                    <h1>Meeting Record Details</h1>
                                </div>
                            </div>

                            <div className="meeting-detail-content">
                                <div className="meeting-detail-top-sections">
                                    {/* Basic Information */}
                                    <section className="detail-section" aria-labelledby="meeting-info-heading">
                                        <h2 id="meeting-info-heading">Meeting Information</h2>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>Event</label>
                                                <span>{meetingRecord.eventId?.name || 'N/A'}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Booth</label>
                                                <span>{meetingRecord.boothId?.name || 'N/A'}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Position</label>
                                                <span>{meetingRecord.appliedPosition || meetingRecord.queueId?.appliedPosition || 'N/A'}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Position Location</label>
                                                <span>{meetingRecord.appliedLocation || meetingRecord.queueId?.appliedLocation || 'N/A'}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Status</label>
                                                <span className={`status-badge status-${meetingRecord.status}`}>
                                                    {formatStatus(meetingRecord.status)}
                                                </span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Start Time</label>
                                                <span>{formatDateTime(meetingRecord.startTime)}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>End Time</label>
                                                <span>{formatDateTime(meetingRecord.endTime)}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Duration</label>
                                                <span>{formatDuration(meetingRecord.duration)}</span>
                                            </div>
                                        </div>
                                    </section>

                                    {/* Participants */}
                                    <section className="detail-section" aria-labelledby="participants-heading">
                                        <h2 id="participants-heading">Participants</h2>
                                        <div className="mr-detail-participants-grid" role="list">
                                            <article className="mr-detail-participant-card" role="listitem">
                                                <h3>Recruiter</h3>
                                                {meetingRecord.status === 'left_with_message' ? (
                                                    <>
                                                        <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Name</span><span>All Recruiters in Booth</span></div>
                                                        <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Email</span><span>{meetingRecord.boothId?.name || 'Booth Message'}</span></div>
                                                        <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Note</span><span>This message is visible to all recruiters in the booth</span></div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Name</span><span>{meetingRecord.recruiterId?.name || 'N/A'}</span></div>
                                                        <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Email</span><span>{meetingRecord.recruiterId?.email || 'N/A'}</span></div>
                                                    </>
                                                )}
                                            </article>
                                            <article className="mr-detail-participant-card" role="listitem">
                                                <h3>Job Seeker</h3>
                                                <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Name</span><span>{meetingRecord.jobseekerId?.name || 'N/A'}</span></div>
                                                <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Email</span><span>{meetingRecord.jobseekerId?.email || 'N/A'}</span></div>
                                                <div className="mr-detail-participant-row">
                                                    <span className="mr-detail-participant-label">Location</span>
                                                    <span>
                                                        {meetingRecord.jobseekerId?.city && meetingRecord.jobseekerId?.state
                                                            ? `${meetingRecord.jobseekerId.city}, ${meetingRecord.jobseekerId.state}`
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                                {(() => {
                                                    const resumeId = meetingRecord.jobSeekerResumeId || meetingRecord.resolvedResume?.resumeId;
                                                    const resumeUrl = meetingRecord.jobSeekerResumeUrl || meetingRecord.resolvedResume?.resumeUrl || meetingRecord.jobseekerId?.resumeUrl;
                                                    if (!resumeId && !resumeUrl) return null;
                                                    return (
                                                        <div className="mr-detail-participant-row mr-detail-participant-row--resume">
                                                            <span className="mr-detail-participant-label">Resume</span>
                                                            <button
                                                                type="button"
                                                                className="btn-view-resume-inline"
                                                                onClick={() => openResumeInNewTab(resumeId || null, resumeUrl || null)}
                                                                aria-label={`View resume for ${meetingRecord.jobseekerId?.name || 'job seeker'}`}
                                                            >
                                                                View
                                                            </button>
                                                        </div>
                                                    );
                                                })()}
                                            </article>
                                            {meetingRecord.interpreterId && (
                                                <article className="mr-detail-participant-card" role="listitem">
                                                    <h3>Interpreter</h3>
                                                    <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Name</span><span>{meetingRecord.interpreterId.name}</span></div>
                                                    <div className="mr-detail-participant-row"><span className="mr-detail-participant-label">Email</span><span>{meetingRecord.interpreterId.email}</span></div>
                                                </article>
                                            )}
                                        </div>
                                    </section>
                                </div>

                                {/* Rating and Feedback */}
                                {(meetingRecord.recruiterRating || meetingRecord.recruiterFeedback) && (
                                    <div className="detail-section">
                                        <h2>Rating & Feedback</h2>
                                        <div className="rating-feedback-container">
                                            {meetingRecord.recruiterRating && (
                                                <div className="rating-display">
                                                    <label>Rating</label>
                                                    <div className="stars-container">
                                                        <span className="stars">{renderStars(meetingRecord.recruiterRating)}</span>
                                                        <span className="rating-number">({meetingRecord.recruiterRating}/5)</span>
                                                    </div>
                                                </div>
                                            )}
                                            {meetingRecord.recruiterFeedback && (
                                                <div className="feedback-display">
                                                    <label>Feedback</label>
                                                    <div className="feedback-text">
                                                        {meetingRecord.recruiterFeedback}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Job Seeker Messages */}
                                {meetingRecord.jobSeekerMessages && meetingRecord.jobSeekerMessages.length > 0 && (
                                    <section className="detail-section" aria-labelledby="messages-heading">
                                        <h2 id="messages-heading">Job Seeker Messages ({meetingRecord.jobSeekerMessages.length})</h2>
                                        <div className="messages-container" role="list">
                                            {meetingRecord.jobSeekerMessages.map((message, index) => (
                                                <article key={index} className="message-item" role="listitem">
                                                    <div className="message-header">
                                                        <span className="message-type">{renderMessageType(message.type)}</span>
                                                        <span className="message-time">{formatDateTime(message.createdAt)}</span>
                                                    </div>
                                                    <div className="message-content">
                                                        {message.type === 'text' ? (
                                                            <p>{message.content}</p>
                                                        ) : message.type === 'audio' ? (
                                                            <div className="media-player">
                                                                <audio 
                                                                    controls 
                                                                    aria-label="Audio message from job seeker"
                                                                    preload="metadata"
                                                                >
                                                                    <source src={message.content} type="audio/webm" />
                                                                    <source src={message.content} type="audio/mp4" />
                                                                    Your browser does not support the audio element.
                                                                </audio>
                                                            </div>
                                                        ) : message.type === 'video' ? (
                                                            <div className="media-player">
                                                                <video 
                                                                    controls 
                                                                    aria-label="Video message from job seeker"
                                                                    preload="metadata"
                                                                >
                                                                    <source src={message.content} type="video/webm" />
                                                                    <source src={message.content} type="video/mp4" />
                                                                    Your browser does not support the video element.
                                                                </video>
                                                            </div>
                                                        ) : (
                                                            <div className="media-message">
                                                                <p>Media file: {message.content}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* Chat Messages */}
                                {meetingRecord.chatMessages && meetingRecord.chatMessages.length > 0 && (
                                    <div className="detail-section">
                                        <h2>Chat Messages ({meetingRecord.chatMessages.length})</h2>
                                        <div className="chat-container">
                                            {meetingRecord.chatMessages.map((message, index) => (
                                                <div key={index} className="chat-message">
                                                    <div className="chat-header">
                                                        <span className="sender-name">
                                                            {message.userId === meetingRecord.recruiterId?._id ? 'Recruiter' :
                                                                message.userId === meetingRecord.jobseekerId?._id ? 'Job Seeker' :
                                                                    'Interpreter'}
                                                        </span>
                                                        <span className="chat-time">{formatDateTime(message.timestamp)}</span>
                                                    </div>
                                                    <div className="chat-content">
                                                        {message.message}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Technical Information */}
                                {!isRecruiter && (
                                    <div className="detail-section">
                                        <h2>Technical Information</h2>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>Room ID</label>
                                                <span>{meetingRecord.twilioRoomId || 'N/A'}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Room SID</label>
                                                <span>{meetingRecord.twilioRoomSid || 'N/A'}</span>
                                            </div>
                                            {meetingRecord.qualityMetrics && (
                                                <>
                                                    <div className="detail-item">
                                                        <label>Connection Quality</label>
                                                        <span>{meetingRecord.qualityMetrics.connectionQuality || 'N/A'}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <label>Audio Quality</label>
                                                        <span>{meetingRecord.qualityMetrics.audioQuality || 'N/A'}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <label>Video Quality</label>
                                                        <span>{meetingRecord.qualityMetrics.videoQuality || 'N/A'}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <label>Dropped Connections</label>
                                                        <span>{meetingRecord.qualityMetrics.droppedConnections || 0}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
